import { useEffect, useRef, useState } from "react";

import { useAtom } from "jotai";
import {
  CheckCircle2,
  ChevronDown,
  CircleDashed,
  Clock3,
  ListTodo,
  PartyPopper,
} from "lucide-react";

import { PaywallDialog } from "src/components/PaywallDialog/PaywallDialog";
import { settingsDialogAtom } from "src/components/SettingsDialog/SettingsDialog";
import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "src/components/ui/command";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "src/components/ui/drawer";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "src/components/ui/hover-card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import { Progress } from "src/components/ui/progress";
import { ScrollArea } from "src/components/ui/scroll-area";
import { useIsMobile } from "src/lib/hooks/useMobile";
import { useOnboarding } from "src/lib/hooks/useOnboarding";
import { openOnboardingAction } from "src/lib/onboarding/actions";
import {
  getOnboardingStateId,
  GLOBAL_ONBOARDING_SCOPE_KEY,
} from "src/lib/onboarding/buildOnboardingState";
import { cn } from "src/lib/utils";

const statusIcon = {
  blocked: CircleDashed,
  action_needed: CircleDashed,
  in_progress: Clock3,
  complete: CheckCircle2,
  skipped: CheckCircle2,
};

const QUICK_START_COMPLETE_DISMISS_KEY = "quick_start_complete_v1";
const APP_NAME = process.env.APP_NAME || "the app";

function TaskRow({
  task,
  loading,
  setOpen,
  setSettingsDialogState,
  setPaywallState,
  skipTask,
  restoreTask,
  hoverEnabled,
}) {
  const Icon = statusIcon[task.status] || CircleDashed;
  const hasAction = Boolean(task.action);
  const isInteractive = hasAction && !task.complete && !loading;
  const showSkipControl = task.canSkip && (!task.complete || task.skipped);
  const showGoToControl =
    hasAction && task.complete && !task.skipped && !showSkipControl;
  const showControl = showSkipControl || showGoToControl;
  const skipLabel = task.skipped ? "Restore" : "Skip";
  const [previewOpen, setPreviewOpen] = useState(false);

  const runTaskAction = () => {
    if (!hasAction || loading) {
      return;
    }

    setOpen(false);
    openOnboardingAction({
      action: task.action,
      setSettingsDialogState,
      setPaywallState,
    });
  };

  return (
    <HoverCard open={previewOpen}>
      <HoverCardTrigger asChild>
        <CommandItem
          value={task.title}
          onSelect={() => {
            if (!isInteractive) {
              return;
            }

            runTaskAction();
          }}
          className={cn(
            "group gap-3 data-[selected=true]:bg-accent/60 data-[selected=true]:text-foreground",
            isInteractive && "cursor-pointer",
            !isInteractive && "cursor-default",
            task.status === "blocked" && "opacity-70"
          )}
          onPointerEnter={() => {
            if (hoverEnabled) {
              setPreviewOpen(true);
            }
          }}
          onPointerLeave={() => setPreviewOpen(false)}
        >
          <div className="flex size-4 shrink-0 items-center justify-center self-center">
            <Icon
              className={cn(
                "size-4",
                task.status === "complete" && "text-primary",
                task.status === "in_progress" && "text-primary",
                task.status === "skipped" && "text-muted-foreground",
                task.status === "blocked" && "text-muted-foreground/70",
                task.status === "action_needed" && "text-muted-foreground"
              )}
            />
          </div>
          <div className="min-w-0 flex flex-1 items-center justify-between gap-3">
            <div
              className={cn(
                "min-w-0 text-sm leading-none font-medium",
                (task.status === "complete" || task.status === "skipped") &&
                  "text-muted-foreground line-through"
              )}
            >
              {task.title}
            </div>

            {showControl && (
              <div className="flex w-[4.75rem] shrink-0 justify-end">
                {showSkipControl && (
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground hover:bg-accent relative z-10 shrink-0 rounded-md border border-transparent px-2 py-1 text-xs transition-colors hover:border-border/70"
                    disabled={loading}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();

                      const action = task.skipped ? restoreTask : skipTask;
                      void action({
                        scopeKey: task.scopeKey,
                        key: task.key,
                        payload: {
                          title: task.title,
                        },
                      });
                    }}
                  >
                    {skipLabel}
                  </button>
                )}

                {showGoToControl && (
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground hover:bg-accent relative z-10 shrink-0 rounded-md border border-transparent px-2 py-1 text-xs transition-colors hover:border-border/70"
                    disabled={loading}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      runTaskAction();
                    }}
                  >
                    Go to
                  </button>
                )}
              </div>
            )}
          </div>
        </CommandItem>
      </HoverCardTrigger>
      <HoverCardContent
        side="left"
        align="center"
        sideOffset={12}
        collisionPadding={16}
        className="w-72 border-border/70 p-4"
        onPointerEnter={() => {
          if (hoverEnabled) {
            setPreviewOpen(true);
          }
        }}
        onPointerLeave={() => setPreviewOpen(false)}
      >
        <div className="space-y-1">
          <div className="text-sm font-semibold">{task.title}</div>
          <div className="text-muted-foreground text-sm leading-5">
            {task.description}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

function TaskList({
  title,
  tasks,
  loading,
  setOpen,
  setSettingsDialogState,
  setPaywallState,
  skipTask,
  restoreTask,
  hoverEnabled,
}) {
  if (!tasks.length) {
    return null;
  }

  return (
    <CommandGroup
      heading={title}
      className="overflow-visible p-0 [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-0 [&_[cmdk-group-heading]]:pb-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.24em]"
    >
      {tasks.map((task) => (
        <TaskRow
          key={task.key}
          task={task}
          loading={loading}
          setOpen={setOpen}
          setSettingsDialogState={setSettingsDialogState}
          setPaywallState={setPaywallState}
          skipTask={skipTask}
          restoreTask={restoreTask}
          hoverEnabled={hoverEnabled}
        />
      ))}
    </CommandGroup>
  );
}

function ChecklistTrigger({ pendingCount, className, ...props }) {
  const allComplete = pendingCount === 0;

  return (
    <Button
      type="button"
      variant="outline"
      size="default"
      className={cn(
        "relative overflow-hidden",
        allComplete && "border-primary/35 bg-primary/8",
        className
      )}
      data-onboarding-id="header-checklist-trigger"
      {...props}
    >
      {allComplete ? (
        <PartyPopper className="text-primary size-4" />
      ) : (
        <ListTodo className="size-4" />
      )}
      <span className="hidden sm:inline">Onboarding</span>
      <Badge
        variant="secondary"
        className={cn(
          "h-5 min-w-5 px-1.5 text-[10px]",
          allComplete && "bg-primary/12 text-primary"
        )}
      >
        {allComplete ? "Done" : pendingCount}
      </Badge>
      <ChevronDown className="size-3.5 opacity-70" />
    </Button>
  );
}

function CompletionCard({ selectedStore, celebrate, loading, onDismiss }) {
  return (
    <div className="border-primary/20 from-primary/12 via-primary/6 relative overflow-hidden rounded-xl border bg-gradient-to-br to-transparent px-3.5 py-3">
      <div className="absolute inset-0 pointer-events-none">
        <span
          className={cn(
            "bg-primary/30 absolute top-3 right-12 size-1.5 rounded-full",
            celebrate && "animate-pulse"
          )}
        />
        <span
          className={cn(
            "bg-primary/20 absolute top-6 right-6 size-2 rounded-full",
            celebrate && "animate-pulse"
          )}
        />
        <span
          className={cn(
            "bg-primary/25 absolute bottom-4 left-12 size-1.5 rounded-full",
            celebrate && "animate-pulse"
          )}
        />
      </div>

      <div className="relative flex items-start gap-3">
        <div className="bg-primary/12 text-primary flex size-9 shrink-0 items-center justify-center rounded-full border border-primary/20">
          <PartyPopper className="size-4" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">All steps complete</div>
          <div className="text-muted-foreground mt-1 text-xs leading-5">
            {selectedStore
              ? `${selectedStore.description || "This store"} is fully set up and ready to use.`
              : "Onboarding is fully complete."}
          </div>
        </div>

        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 shrink-0 px-2.5 text-xs"
          disabled={loading}
          onClick={onDismiss}
        >
          Hide
        </Button>
      </div>
    </div>
  );
}

function ChecklistContent({
  selectedStore,
  onboarding,
  loading,
  setOpen,
  setSettingsDialogState,
  setPaywallState,
  skipTask,
  restoreTask,
  hoverEnabled,
  mobile = false,
  allComplete = false,
  celebrate = false,
  onDismissComplete,
}) {
  return (
    <>
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-semibold">Onboarding</div>
          <Badge variant="outline" className="text-[10px]">
            {onboarding.completeCount}/{onboarding.totalCount}
          </Badge>
        </div>
        <div className="text-muted-foreground text-xs leading-5">
          {selectedStore
            ? `Setup progress for ${selectedStore.description || "this store"}`
            : `Complete your initial ${APP_NAME} setup.`}
        </div>
      </div>

      <Progress value={onboarding.progressPercent} className="h-1.5" />

      {allComplete && (
        <CompletionCard
          selectedStore={selectedStore}
          celebrate={celebrate}
          loading={loading}
          onDismiss={onDismissComplete}
        />
      )}

      <Command
        className={cn(
          "overflow-visible rounded-none bg-transparent",
          !mobile && "min-h-0 flex-1"
        )}
      >
        <ScrollArea
          className={cn(
            "-mr-3 pr-3",
            mobile ? "max-h-[55vh]" : "min-h-0 flex-1"
          )}
        >
          <CommandList className="max-h-none">
            <TaskList
              title="Required"
              tasks={onboarding.requiredTasks}
              loading={loading}
              setOpen={setOpen}
              setSettingsDialogState={setSettingsDialogState}
              setPaywallState={setPaywallState}
              skipTask={skipTask}
              restoreTask={restoreTask}
              hoverEnabled={hoverEnabled}
            />
            {!!onboarding.requiredTasks.length &&
              !!onboarding.recommendedTasks.length && (
                <CommandSeparator className="my-2" />
              )}
            <TaskList
              title="Optional"
              tasks={onboarding.recommendedTasks}
              loading={loading}
              setOpen={setOpen}
              setSettingsDialogState={setSettingsDialogState}
              setPaywallState={setPaywallState}
              skipTask={skipTask}
              restoreTask={restoreTask}
              hoverEnabled={hoverEnabled}
            />
          </CommandList>
        </ScrollArea>
      </Command>
    </>
  );
}

export function OnboardingChecklistButton() {
  const [open, setOpen] = useState(false);
  const [hoverEnabled, setHoverEnabled] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [paywallState, setPaywallState] = useState({
    open: false,
    source: "onboarding",
  });
  const previousPendingCountRef = useRef(null);
  const [, setSettingsDialogState] = useAtom(settingsDialogAtom);
  const isMobile = useIsMobile();
  const {
    selectedStore,
    onboarding,
    isStoreAdmin,
    loading,
    onboardingStatesById,
    dismissPrompt,
    skipTask,
    restoreTask,
  } = useOnboarding();

  const pendingTasks = onboarding.tasks.filter((task) => !task.complete);
  const pendingCount = pendingTasks.length;
  const allComplete = onboarding.totalCount > 0 && pendingCount === 0;
  const completionDismissed = Boolean(
    onboardingStatesById[
      getOnboardingStateId(
        GLOBAL_ONBOARDING_SCOPE_KEY,
        QUICK_START_COMPLETE_DISMISS_KEY
      )
    ]?.dismissedAt
  );

  useEffect(() => {
    const previousPendingCount = previousPendingCountRef.current;

    if (
      onboarding.totalCount > 0 &&
      pendingCount === 0 &&
      previousPendingCount !== null &&
      previousPendingCount > 0
    ) {
      setCelebrate(true);

      const timeoutId = window.setTimeout(() => {
        setCelebrate(false);
      }, 4000);

      previousPendingCountRef.current = pendingCount;

      return () => {
        window.clearTimeout(timeoutId);
      };
    }

    if (pendingCount > 0 && celebrate) {
      setCelebrate(false);
    }

    previousPendingCountRef.current = pendingCount;
  }, [celebrate, onboarding.totalCount, pendingCount]);

  if (selectedStore && !isStoreAdmin) {
    return null;
  }

  if (!onboarding.totalCount) {
    return null;
  }

  if (allComplete && completionDismissed) {
    return null;
  }

  const dismissCompletedChecklist = async () => {
    await dismissPrompt({
      scopeKey: GLOBAL_ONBOARDING_SCOPE_KEY,
      key: QUICK_START_COMPLETE_DISMISS_KEY,
      payload: {
        dismissedAfterComplete: true,
      },
    });
    setOpen(false);
  };

  const paywallDialog = (
    <PaywallDialog
      source={paywallState.source}
      open={paywallState.open}
      onOpenChange={(nextOpen) =>
        setPaywallState((prev) => ({
          ...prev,
          open: nextOpen,
        }))
      }
    />
  );

  if (isMobile) {
    return (
      <>
        <Drawer
          open={open}
          onOpenChange={(nextOpen) => {
            setOpen(nextOpen);
            setHoverEnabled(false);
          }}
        >
          <DrawerTrigger asChild>
            <ChecklistTrigger pendingCount={pendingCount} />
          </DrawerTrigger>
          <DrawerContent className="max-h-[85vh]">
            <DrawerHeader className="sr-only">
              <DrawerTitle>Onboarding</DrawerTitle>
              <DrawerDescription>
                {selectedStore
                  ? `Setup progress for ${selectedStore.description || "this store"}`
                  : `Complete your initial ${APP_NAME} setup.`}
              </DrawerDescription>
            </DrawerHeader>
            <div className="flex flex-col gap-4 px-4 pb-4">
              <ChecklistContent
                selectedStore={selectedStore}
                onboarding={onboarding}
                loading={loading}
                setOpen={setOpen}
                setSettingsDialogState={setSettingsDialogState}
                setPaywallState={setPaywallState}
                skipTask={skipTask}
                restoreTask={restoreTask}
                hoverEnabled={false}
                mobile
                allComplete={allComplete}
                celebrate={celebrate}
                onDismissComplete={() => {
                  void dismissCompletedChecklist();
                }}
              />
            </div>
          </DrawerContent>
        </Drawer>
        {paywallDialog}
      </>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        setHoverEnabled(false);
      }}
    >
      <PopoverTrigger asChild>
        <ChecklistTrigger pendingCount={pendingCount} />
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        className="flex max-h-(--radix-popover-content-available-height) w-[21.5rem] max-w-[calc(100vw-2rem)] flex-col gap-4 overflow-hidden rounded-2xl p-4"
        onPointerMove={() => {
          if (!hoverEnabled) {
            setHoverEnabled(true);
          }
        }}
      >
        <ChecklistContent
          selectedStore={selectedStore}
          onboarding={onboarding}
          loading={loading}
          setOpen={setOpen}
          setSettingsDialogState={setSettingsDialogState}
          setPaywallState={setPaywallState}
          skipTask={skipTask}
          restoreTask={restoreTask}
          hoverEnabled={hoverEnabled}
          allComplete={allComplete}
          celebrate={celebrate}
          onDismissComplete={() => {
            void dismissCompletedChecklist();
          }}
        />
      </PopoverContent>
      {paywallDialog}
    </Popover>
  );
}
